import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Service, { inject } from '@ember/service';
import { getOwner } from '@ember/application';
import { tracked } from '@glimmer/tracking';
import { set } from '@ember/object';
import { task } from 'ember-concurrency';
import { beebeeAjaxGet } from '../lib/utility/ajax.js';
import '@ember/utils';
import 'moment-timezone';
import 'ember-inflector';
import '../lib/utility/province.js';
import getConfig from '../lib/get-config.js';

var _dec, _dec2, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4;

/**
 * Normalize a contact (record or bare id) to the map key.
 * Ember Data ids are strings while callers hand us numbers, so an id compared
 * with `===` to a number never matches — everything is keyed as a string.
 * @param {Object|string|number} contact - contact record or its id
 * @returns {string|null} the key, or null when there is no usable id
 */
function keyFor(contact) {
  const id = contact && typeof contact === 'object' ? contact.id : contact;
  return id === null || id === undefined || id === '' ? null : String(id);
}

/**
 * Give every consent row its single-icon variant, as the panel expects.
 * @param {Array} consensi - consent rows from the API
 * @returns {void}
 */
function processConsensi(consensi) {
  if (!Array.isArray(consensi)) {
    return;
  }
  consensi.forEach(item => {
    if (item.icon_name) {
      item.icon_name_single = item.icon_name.replace(/-?square-?/g, '');
    }
  });
}

/**
 * Attach the per-option acceptance history to every row of a consent list.
 * @param {Array} consensi - consent rows from the API
 * @returns {void}
 */
function attachHistory(consensi) {
  if (!Array.isArray(consensi)) {
    return;
  }
  consensi.forEach(item => {
    item.history = consensi.filter(c => c.option_name === item.option_name).map(c => ({
      id_: c.id_,
      option_name: c.option_name,
      class_name: c.class_name,
      icon_name: c.icon_name,
      icon_name_single: c.icon_name_single,
      accepted_date: c.accepted_date,
      spunta_id: c.spunta_id
    }));
  });
}
let PrivacyService = (_dec = task({
  enqueue: true
}), _dec2 = task({
  enqueue: true
}), _class = class PrivacyService extends Service {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "session", _descriptor, this);
    /** contact key → the `/consensi` payload every panel of that contact reads. */
    _initializerDefineProperty(this, "_payloads", _descriptor2, this);
    /** contact key → bump counter consumed by the derived getters. */
    _initializerDefineProperty(this, "_revisions", _descriptor3, this);
    /** contact key → true once a FEA document was signed in this session. */
    _initializerDefineProperty(this, "_feaSigned", _descriptor4, this);
    /**
     * contact key → the project scope the payload was fetched with, so a refresh
     * triggered by a socket event keeps the scope the panel asked for instead of
     * silently falling back to the contact-wide one.
     * Not tracked: nothing renders it.
     */
    this._lastProject = new Map();
  }
  /**
   * The Ember Data store, looked up lazily instead of injected: this service is
   * instantiated in addon test containers that register no `service:store`, and a
   * declared injection is validated there even when it is never read.
   * @returns {Object|null} the store, or null when the container has none
   */
  get store() {
    const owner = getOwner(this);
    return owner?.hasRegistration?.('service:store') ? owner.lookup('service:store') : null;
  }
  /**
   * The payload every panel of this contact renders.
   * @param {Object|string|number} contact - contact record or id
   * @returns {Object|null} the `/consensi` payload, or null when not loaded yet
   */
  dataFor(contact) {
    const key = keyFor(contact);
    return key && this._payloads.get(key) || null;
  }

  /**
   * Revision of the consent data of this contact. Derived getters consume it so
   * that an in-place edit of a row (which keeps the payload identity) still
   * invalidates counters and filtered lists in EVERY mounted panel.
   * @param {Object|string|number} contact - contact record or id
   * @returns {number} monotonic revision
   */
  revisionFor(contact) {
    const key = keyFor(contact);
    return key && this._revisions.get(key) || 0;
  }

  /**
   * FEA (advanced electronic signature) authorization state of a contact.
   * @param {Object|string|number} contact - contact record or id
   * @returns {boolean|null} true/false when known, null when there is no data
   */
  feaAcceptedFor(contact) {
    const key = keyFor(contact);
    if (key && this._feaSigned.get(key)) {
      // Signed on a device during this session: the server may not have caught
      // up yet, and re-asking for an authorization just given is the bug.
      return true;
    }
    const data = this.dataFor(contact);
    if (!data) {
      return null;
    }
    const fea = Array.isArray(data.consensi) ? data.consensi.find(item => item.id_ === 0) : null;
    return fea ? fea.class_name === 'accepted' : false;
  }

  /**
   * Store a freshly fetched payload and invalidate every reader.
   * @param {Object|string|number} contact - contact record or id
   * @param {Object} data - the `/consensi` payload
   * @returns {void}
   */
  setData(contact, data) {
    const key = keyFor(contact);
    if (!key) {
      return;
    }
    const payloads = new Map(this._payloads);
    payloads.set(key, data || null);
    this._payloads = payloads;
    this.touch(contact);
  }

  /**
   * Bump the revision so every panel of this contact recomputes its derived
   * state. Call it after mutating a row in place.
   * @param {Object|string|number} contact - contact record or id
   * @returns {void}
   */
  touch(contact) {
    const key = keyFor(contact);
    if (!key) {
      return;
    }
    const revisions = new Map(this._revisions);
    revisions.set(key, (revisions.get(key) || 0) + 1);
    this._revisions = revisions;
  }

  /**
   * Record that a FEA document was signed on a device. This flips the GATE that
   * decides whether the UI asks again for the FEA authorization; it does NOT
   * paint any consent row as accepted — the row state stays the server's.
   * @param {Object|string|number} contact - contact record or id
   * @returns {void}
   */
  markFeaSigned(contact) {
    this.setFeaAccepted(contact, true);
  }

  /**
   * Set the FEA authorization gate of a contact explicitly. Used both by a
   * device signature (true) and by an operator editing the FEA row in the panel,
   * which must be able to take the gate back down — a gate stuck on `true` would
   * let a module be requested without a live FEA authorization.
   * @param {Object|string|number} contact - contact record or id
   * @param {boolean} accepted - the new gate state
   * @returns {void}
   */
  setFeaAccepted(contact, accepted) {
    const key = keyFor(contact);
    if (!key) {
      return;
    }
    const feaSigned = new Map(this._feaSigned);
    if (accepted) {
      feaSigned.set(key, true);
    } else {
      feaSigned.delete(key);
    }
    this._feaSigned = feaSigned;

    // Mirror on the record for any consumer still reading contact.acceptedFEA.
    if (contact && typeof contact === 'object' && !contact.isDestroyed && !contact.isDestroying) {
      set(contact, 'acceptedFEA', !!accepted);
    }
    this.touch(contact);
  }

  /**
   * Fetch the consent payload of a contact and publish it to every panel.
   * @param {Object} contact - contact record
   * @param {string|number} [projectId] - case scope, undefined for contact-wide
   * @returns {Object|null} the payload
   */
  // Decorator+generator form on purpose: the async-arrow FIELD form
  // (`= task(async …)`) needs the ember-concurrency field transform, which is not
  // reliably applied in app builds — the field then holds a raw TaskProperty and
  // `.perform` is undefined.
  *loadTask(contact, projectId) {
    const key = keyFor(contact);
    if (!key) {
      return null;
    }
    const scope = typeof projectId === 'string' || typeof projectId === 'number' ? projectId : undefined;
    const data = yield beebeeAjaxGet(`${getConfig(this).APP.urlApi}/consensi`, this.session.token, {
      contact_id: key,
      project_id: scope
    });
    const consensi = data?.consensi;
    attachHistory(consensi);
    processConsensi(consensi);
    const projects = data?.projects;
    if (Array.isArray(projects)) {
      projects.forEach(project => {
        processConsensi(project?.consensi);
        attachHistory(project?.consensi);
      });
    }
    this._lastProject.set(key, scope);
    this.setData(contact, data ?? null);

    // Mirror the server truth on the record for any consumer still reading
    // contact.acceptedFEA directly.
    const fea = Array.isArray(consensi) ? consensi.find(item => item.id_ === 0) : null;
    if (contact && typeof contact === 'object' && !contact.isDestroyed && !contact.isDestroying) {
      set(contact, 'acceptedFEA', this.feaAcceptedFor(contact) ?? fea?.class_name === 'accepted');
      set(contact, 'privacyAPI', data ?? null);
    }
    return data ?? null;
  }

  /**
   * A document was signed on a device for this contact: pull the new file into
   * the store (the signed module has to show up in the attachments) and refetch
   * the consents so every mounted panel repaints from server truth.
   * @param {Object} contact - contact record
   * @param {number} [consensoId] - id of the signed consent, 0 for the FEA
   * @returns {void}
   */
  *refreshAfterSignTask(contact, consensoId) {
    const key = keyFor(contact);
    if (!key) {
      return;
    }
    if (consensoId === 0) {
      this.markFeaSigned(contact);
    }

    // contact.files is a SYNC hasMany: it is only refreshed by reloading the
    // owner record, there is no relationship reload to call.
    const store = this.store;
    try {
      if (store) {
        yield store.findRecord('contact', key, {
          reload: true
        });
      }
    } catch {
      // A failed reload must not stop the consent refresh below: the checkboxes
      // are the part the operator is looking at.
    }
    yield this.loadTask.perform(contact, this._lastProject.get(key));
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_payloads", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return new Map();
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "_revisions", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return new Map();
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "_feaSigned", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return new Map();
  }
}), _applyDecoratedDescriptor(_class.prototype, "loadTask", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "loadTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "refreshAfterSignTask", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "refreshAfterSignTask"), _class.prototype), _class);

export { PrivacyService as default };
//# sourceMappingURL=privacy.js.map
